import React, { useEffect, useMemo, useRef, useState } from 'react';
import useCopyText from '../../hooks/useCopyText';
import type { EmailTemplateMacro } from '../../types/emailTemplate';
import {
  filterMacroCatalog,
  findMacroDefinition,
  groupMacroCatalog,
  macroToken,
} from '../../utils/emailTemplateMacros';

export type EmailTemplateMacroInsertTarget = 'subject' | 'body';

interface EmailTemplateMacroPickerProps {
  catalog: EmailTemplateMacro[];
  loading?: boolean;
  onInsert: (macro: string, target: EmailTemplateMacroInsertTarget) => void;
}

interface EmailTemplateMacroListProps {
  catalog: EmailTemplateMacro[];
  usedMacros: string[];
}

const USED_MACROS_COLLAPSE_THRESHOLD = 4;

export const EmailTemplateMacroPicker: React.FC<EmailTemplateMacroPickerProps> = ({
  catalog,
  loading = false,
  onInsert,
}) => {
  const { copyText } = useCopyText();
  const containerRef = useRef<HTMLDivElement>(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [insertTarget, setInsertTarget] = useState<EmailTemplateMacroInsertTarget>('body');
  const [search, setSearch] = useState('');

  const filteredCatalog = useMemo(() => filterMacroCatalog(catalog, search), [catalog, search]);
  const groupedCatalog = useMemo(() => groupMacroCatalog(filteredCatalog), [filteredCatalog]);

  useEffect(() => {
    if (!pickerOpen)
    {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node))
      {
        setPickerOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [pickerOpen]);

  const handleInsert = (definition: EmailTemplateMacro) => {
    onInsert(macroToken(definition.key), insertTarget);
    setPickerOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setPickerOpen((open) => !open)}
        aria-expanded={pickerOpen}
        disabled={loading || catalog.length === 0}
        className="inline-flex items-center gap-2 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
          />
        </svg>
        Macros disponíveis
      </button>

      {loading && (
        <p className="absolute right-0 mt-1 whitespace-nowrap text-xs text-gray-500">
          Carregando variáveis...
        </p>
      )}

      {pickerOpen && (
        <div className="absolute right-0 z-20 mt-2 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-3">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar variável..."
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              autoFocus
            />
            <div className="mt-2 flex gap-1">
              <TargetToggle
                active={insertTarget === 'subject'}
                label="Assunto"
                onClick={() => setInsertTarget('subject')}
              />
              <TargetToggle
                active={insertTarget === 'body'}
                label="Corpo"
                onClick={() => setInsertTarget('body')}
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredCatalog.length === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-500">Nenhuma variável encontrada.</p>
            ) : (
              Array.from(groupedCatalog.entries()).map(([group, items]) => (
                <div key={group}>
                  <p className="sticky top-0 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {group}
                  </p>
                  {items.map((item) => (
                    <MacroPickerRow
                      key={item.key}
                      item={item}
                      onInsert={() => handleInsert(item)}
                      onCopy={() => void copyText(macroToken(item.key))}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const EmailTemplateMacroList: React.FC<EmailTemplateMacroListProps> = ({
  catalog,
  usedMacros,
}) => {
  const { copyText } = useCopyText();
  const [usedExpanded, setUsedExpanded] = useState(
    usedMacros.length <= USED_MACROS_COLLAPSE_THRESHOLD
  );

  useEffect(() => {
    if (usedMacros.length <= USED_MACROS_COLLAPSE_THRESHOLD)
    {
      setUsedExpanded(true);
    }
  }, [usedMacros.length]);

  const renderUsedMacroRow = (token: string) => {
    const definition = findMacroDefinition(token, catalog);

    return (
      <div
        key={token}
        className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0"
      >
        <div className="min-w-0">
          <code className="text-xs font-semibold text-indigo-700">{token}</code>
          {definition && (
            <p className="truncate text-xs text-gray-500">{definition.description}</p>
          )}
        </div>
        <button
          type="button"
          title="Copiar variável"
          onClick={() => void copyText(token)}
          className="shrink-0 rounded px-2 py-1 text-xs text-gray-600 transition hover:bg-gray-100"
        >
          Copiar
        </button>
      </div>
    );
  };

  if (usedMacros.length === 0)
  {
    return null;
  }

  return (
    <div className="rounded border border-gray-200 bg-gray-50">
      <div className="border-b border-gray-200 px-3 py-2">
        <p className="text-sm font-medium text-gray-700">Variáveis do template</p>
        <p className="text-xs text-gray-500">
          Placeholders em uso no assunto ou no corpo do e-mail.
        </p>
      </div>

      <div className="px-3 py-2">
        <button
          type="button"
          onClick={() => setUsedExpanded((expanded) => !expanded)}
          className="flex w-full items-center justify-between text-left text-sm text-gray-700"
        >
          <span>
            Em uso neste template
            <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
              {usedMacros.length}
            </span>
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 text-gray-500 transition ${usedExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {usedExpanded && (
          <div className="mt-2 overflow-hidden rounded border border-gray-200 bg-white">
            <div className="max-h-40 overflow-y-auto">{usedMacros.map(renderUsedMacroRow)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const TargetToggle: React.FC<{ active: boolean; label: string; onClick: () => void }> = ({
  active,
  label,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded px-2.5 py-1 text-xs font-medium transition ${
      active
        ? 'bg-indigo-600 text-white'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {label}
  </button>
);

const MacroPickerRow: React.FC<{
  item: EmailTemplateMacro;
  onInsert: () => void;
  onCopy: () => void;
}> = ({ item, onInsert, onCopy }) => (
  <div className="flex items-start justify-between gap-2 px-3 py-2 hover:bg-gray-50">
    <div className="min-w-0">
      <code className="text-xs font-semibold text-indigo-700">{macroToken(item.key)}</code>
      <p className="text-sm text-gray-800">{item.label}</p>
      <p className="text-xs text-gray-500">{item.description}</p>
    </div>
    <div className="flex shrink-0 gap-1">
      <button
        type="button"
        onClick={onCopy}
        className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
      >
        Copiar
      </button>
      <button
        type="button"
        onClick={onInsert}
        className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
      >
        Inserir
      </button>
    </div>
  </div>
);

export default EmailTemplateMacroList;
